// Per-hotel theming from the hotel's `webapptheme` (returned by the same v1/hotels/:domain call
// fetchpaymentDetails already makes), mirroring web_check-in / frontdesk_improved / admin-panel-v2.
//
// Every universal token (src/theme/tokens.js) is written onto :root: colors as --color-* "R G B"
// triplets (so Tailwind alpha modifiers like bg-secondary/10 work), radii as --radius-*, and the
// font as --font-sans. Tokens the hotel doesn't set fall back to the Shashi defaults.

import {
  DEFAULT_COLORS,
  DEFAULT_RADIUS,
  DEFAULT_FONT_FAMILY,
  toCssVar,
  toRadiusVar,
} from "./tokens";

const STORAGE_KEY = "webAppTheme";
const FONT_LINK_ID = "theme-font-link";

const toTriplet = (value) =>
  typeof value === "string" ? value.trim().split(/\s+/).join(" ") || undefined : undefined;

/** Hotel colors merged over the defaults, as normalized "R G B" triplets. */
export const resolveColors = (theme) => {
  const colors = { ...DEFAULT_COLORS };
  Object.keys(DEFAULT_COLORS).forEach((key) => {
    const value = toTriplet(theme?.colors?.[key]);
    if (value) colors[key] = value;
  });
  return colors;
};

/** Hotel radii merged over the defaults. */
const resolveRadius = (theme) => {
  const radius = { ...DEFAULT_RADIUS };
  Object.keys(DEFAULT_RADIUS).forEach((key) => {
    const value = theme?.radius?.[key];
    if (typeof value === "string" && value.trim()) radius[key] = value.trim();
  });
  return radius;
};

export const resolveFontFamily = (theme) =>
  (typeof theme?.fontFamily === "string" && theme.fontFamily.trim()) || DEFAULT_FONT_FAMILY;

/** "R G B" triplet -> "rgb(R, G, B)", the form MUI's color utilities can parse. */
export const tripletToRgb = (triplet) => `rgb(${triplet.split(" ").join(", ")})`;

/**
 * The hotel's logo: webapptheme.logoUrl, falling back to the hotel record's hotelLogoBlack.
 * A relative logoUrl (e.g. "/logos/shashi.svg") points at another app's bundled assets, so only absolute URLs are used.
 */
export const getHotelLogo = (hotelDetails) => {
  const themeLogo = hotelDetails?.webapptheme?.logoUrl;
  return (/^https?:\/\//i.test(themeLogo || "") && themeLogo) || hotelDetails?.hotelLogoBlack;
};

function applyFontImport(fontImportUrl) {
  let link = document.getElementById(FONT_LINK_ID);
  if (!fontImportUrl) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = fontImportUrl;
}

// fallbackFaviconUrl is the hotel's plain logo, used when the theme has no faviconUrl,
// the same fallback frontdesk and admin-panel use.
export function applyWebAppTheme(theme, fallbackFaviconUrl) {
  const root = document.documentElement;
  Object.entries(resolveColors(theme)).forEach(([key, value]) => {
    root.style.setProperty(toCssVar(key), value);
  });
  Object.entries(resolveRadius(theme)).forEach(([key, value]) => {
    root.style.setProperty(toRadiusVar(key), value);
  });
  root.style.setProperty("--font-sans", resolveFontFamily(theme));
  applyFontImport(theme?.fontImportUrl);

  const faviconUrl = theme?.faviconUrl || fallbackFaviconUrl;
  if (faviconUrl) {
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement("link");
      favicon.rel = "icon";
      document.head.appendChild(favicon);
    }
    favicon.href = faviconUrl;
  }
}

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Apply the theme from the hotel details response and cache it for the next first paint. */
export function applyHotelTheme(hotelDetails) {
  const cached = {
    theme: hotelDetails?.webapptheme || null,
    fallbackFaviconUrl: getHotelLogo(hotelDetails) || null,
  };
  applyWebAppTheme(cached.theme, cached.fallbackFaviconUrl);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // storage unavailable (private mode etc.) - theme still applied for this session
  }
}

/** The webapptheme cached from the last visit, for the MUI palette before hotel details load. */
export const getCachedHotelTheme = () => readCache()?.theme || null;

/**
 * Paint the theme before the first render: the cached hotel theme if there is one, otherwise
 * the defaults, so the --color-* variables always exist.
 */
export function applyInitialTheme() {
  const cached = readCache();
  applyWebAppTheme(cached?.theme, cached?.fallbackFaviconUrl);
}
